// 업로드 전 브라우저에서 이미지 용량 줄이기.
//
// 왜 서버가 아니라 브라우저인가:
//   multer-s3 가 파일을 곧장 S3 로 흘려보내는 구조라 서버 중간에 손댈 자리가 없고,
//   CloudFront 도 리사이즈 파라미터를 지원하지 않는다(?w=400 이 원본과 바이트 동일).
//   브라우저에서 줄이면 업로드 자체도 빨라진다.
//
// 설계 원칙 — 업로드를 절대 막지 않는다.
//   어떤 이유로든(디코딩 실패, 타임아웃, 예외) 원본 File 을 그대로 돌려준다.
//   압축은 '있으면 좋은 것'이지 업로드의 전제조건이 아니다.

const DEFAULTS = {
  maxEdge: 1200,          // 긴 변 상한. 배너처럼 크게 보이는 건 호출부에서 1600 을 넘긴다
  quality: 0.8,           // JPEG 품질
  skipUnder: 200 * 1024,  // 이미 작은 파일은 재인코딩이 손해 (커지거나 화질만 깎인다)
  timeoutMs: 10000,       // 디코딩/인코딩이 멈춰도 업로드를 붙잡지 않는다
};

// 압축하면 안 되는 파일. 손상 위험이 크거나 이득이 없다.
const shouldSkip = (file, skipUnder) => {
  if (!file || !file.type) return true;
  // 동영상 — 배너는 이미지와 같은 엔드포인트를 쓴다. canvas 로 다룰 수도 없다
  if (!file.type.startsWith('image/')) return true;
  // 벡터 — 래스터화하면 되돌릴 수 없다
  if (file.type === 'image/svg+xml') return true;
  // 애니메이션 GIF 는 canvas 재인코딩 시 첫 프레임만 남는다.
  // 프레임 수를 세는 검사는 오탐 시 손님 자산이 조용히 망가져 위험이 비대칭이라,
  // GIF 는 통째로 건너뛴다 (메뉴 사진에 GIF 는 드물다)
  if (file.type === 'image/gif') return true;
  if (file.size <= skipUnder) return true;
  return false;
};

// 확장자만 교체. S3 key 가 originalname 의 확장자를 쓰므로 실제 바이트와 맞춰야 한다.
const renameExt = (name, ext) => {
  const base = String(name || 'image').replace(/\.[^.]+$/, '');
  return `${base}.${ext}`;
};

// createImageBitmap 우선, 안 되면 <img> 폴백.
// 아이폰 HEIC 는 대부분의 브라우저가 디코딩하지 못해 여기서 전부 실패하고,
// 호출부는 원본을 그대로 업로드한다 — 지금과 동일한 동작이라 회귀가 아니다.
const decode = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      // imageOrientation: 아이폰 사진의 EXIF 회전을 반영한다
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (_) {
      try {
        // 옵션 객체를 모르는 구형 브라우저 대비
        return await createImageBitmap(file);
      } catch (_) { /* 아래 폴백으로 */ }
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    if (img.decode) await img.decode();
    else await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
};

// 축소된 canvas 에 투명 픽셀이 있는지 표본 검사.
// 투명 PNG 를 JPEG 로 바꾸면 투명 부분이 검게 나오므로 포맷 결정에 필요하다.
// 이미 축소된 canvas 라 검사 비용이 작다.
const hasAlpha = (ctx, w, h) => {
  try {
    const { data } = ctx.getImageData(0, 0, w, h);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
  } catch (_) {
    // 어떤 이유로든 못 읽으면 안전하게 '투명하다'고 본다 (PNG 유지)
    return true;
  }
  return false;
};

const toBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const compress = async (file, opt) => {
  const source = await decode(file);
  try {
    const sw = source.width || source.naturalWidth;
    const sh = source.height || source.naturalHeight;
    if (!sw || !sh) return file;

    // 긴 변이 상한을 넘을 때만 축소한다. 절대 확대하지 않는다.
    const scale = Math.min(1, opt.maxEdge / Math.max(sw, sh));
    const w = Math.round(sw * scale);
    const h = Math.round(sh * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, w, h);

    // 투명하면 PNG 유지, 아니면 JPEG.
    // scale === 1 이어도 재인코딩은 진행한다 — 크기는 작은데 인코딩이 나빠
    // 2MB 가 넘는 경우가 실제로 있었다.
    const alpha = hasAlpha(ctx, w, h);
    const mime = alpha ? 'image/png' : 'image/jpeg';
    const ext = alpha ? 'png' : 'jpg';

    const blob = await toBlob(canvas, mime, opt.quality);
    if (!blob) return file;

    // 압축이 오히려 커지면 원본을 쓴다
    if (blob.size >= file.size) return file;

    return new File([blob], renameExt(file.name, ext), {
      type: blob.type,
      lastModified: file.lastModified || Date.now(),
    });
  } finally {
    if (source && typeof source.close === 'function') source.close();
  }
};

/**
 * 이미지를 줄여서 새 File 을 돌려준다.
 * 실패하거나 줄일 필요가 없으면 원본 File 을 그대로 돌려준다 (throw 하지 않는다).
 *
 * @param {File} file
 * @param {{maxEdge?:number, quality?:number, skipUnder?:number, timeoutMs?:number}} options
 * @returns {Promise<File>}
 */
export const compressImageFile = async (file, options = {}) => {
  const opt = { ...DEFAULTS, ...options };
  if (shouldSkip(file, opt.skipUnder)) return file;

  try {
    const timeout = new Promise((_, rej) =>
      setTimeout(() => rej(new Error('압축 시간 초과')), opt.timeoutMs)
    );
    const out = await Promise.race([compress(file, opt), timeout]);
    if (out !== file) {
      console.log(
        `[이미지 압축] ${file.name} ${Math.round(file.size / 1024)}KB → ${out.name} ${Math.round(out.size / 1024)}KB`
      );
    }
    return out;
  } catch (e) {
    console.warn('[이미지 압축] 실패 — 원본 그대로 업로드합니다:', e.message);
    return file;
  }
};

export default { compressImageFile };
